export const gitContent = /* html */ `

<!-- SETUP -->

<div data-id="git-setup" class="level1-container">
  <h1 class="header">Setup</h1>

  <div data-id="setup-git bash">
    <h2>Git Bash</h2>
    <div class="info">
      Bash is the default shell on Linux and macOS.<br>
      Git is designed to run on a Unix-based interface (like bash).<br>
      Windows Command Prompt is not Unix-based.<br>
      Therefore on Windows we use <b>Git Bash</b>, which emulates Bash.
    </div>
  </div>

  <div data-id="setup-.gitignore">
    <h2>.gitignore</h2>

    <div class="info">
      A hidden file used to tell Git which files and folders to ignore.<br>
      This is usually done for private files that we don't want to share in public,
      or for system files (like <b>.DS_Store</b> on macOS).
      <br><br>
      See gitignore.io for the most common .gitignore content for your project.
    </div>

    <pre><code class="language-bash">
  # Foldernames should end in <b>/</b>
  # Otherwise it will ignore both files with that name and folders with that name,
  # while we might just want to ignore the folder.
  foldername/

  # You can use wildcards
  *.log

  # Common hidden private system file on MacOS
  .DS_Store
    </code></pre>
  </div>

  <div data-id="setup-basic linux commands">
    <h2>Basic Linux commands</h2>

    <div class="info">
      Below are common terminal commands you will use in Git workflows.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:22%">
        <col style="width:18%">
        <col style="width:60%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Full</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>ls</b></td>
          <td>list</td>
          <td>shows the content of a directory/folder</td>
        </tr>
        <tr>
          <td><b>ls &lt;foldername&gt;</b></td>
          <td></td>
          <td>shows the content of the given directory/folder</td>
        </tr>
        <tr>
          <td><b>ls -a</b></td>
          <td>all</td>
          <td>shows hidden files as well</td>
        </tr>
        <tr>
          <td><b>ls -l</b></td>
          <td>long</td>
          <td>long format (shows detailed info)</td>
        </tr>
        <tr>
          <td><b>~</b></td>
          <td>home</td>
          <td>home directory. On Windows it's <b>c:/users/&lt;youruser&gt;</b></td>
        </tr>
        <tr>
          <td><b>open .</b></td>
          <td></td>
          <td>opens the current directory in Finder (macOS)</td>
        </tr>
        <tr>
          <td><b>start .</b></td>
          <td></td>
          <td>opens the current directory in Explorer (Windows)</td>
        </tr>
        <tr>
          <td><b>open &lt;filepath&gt;</b></td>
          <td></td>
          <td>opens the given directory in Finder (macOS)</td>
        </tr>
        <tr>
          <td><b>start &lt;filepath/filename&gt;</b></td>
          <td></td>
          <td>opens the given directory in Explorer, or opens the given file (Windows)</td>
        </tr>
        <tr>
          <td><b>clear</b></td>
          <td></td>
          <td>clears the terminal</td>
        </tr>
        <tr>
          <td><b>pwd</b></td>
          <td>print working directory</td>
          <td>prints the path to the current directory</td>
        </tr>
        <tr>
          <td><b>cd &lt;directory&gt;</b></td>
          <td>change directory</td>
          <td>opens the given folder</td>
        </tr>
        <tr>
          <td><b>cd ..</b></td>
          <td>parent directory</td>
          <td>moves upward one directory</td>
        </tr>
        <tr>
          <td><b>touch &lt;filepath/filename.extension&gt;</b></td>
          <td></td>
          <td>(default path is current) creates (a) file(s) with the given name and extension</td>
        </tr>
        <tr>
          <td><b>mkdir &lt;filepath/foldername&gt;</b></td>
          <td>make directory</td>
          <td>(default path is current) creates (a) folder(s) with the given name</td>
        </tr>
        <tr>
          <td><b>rm &lt;filename&gt;</b></td>
          <td>remove</td>
          <td>removes the given file(s)</td>
        </tr>
        <tr>
          <td><b>rm -rf &lt;foldername&gt;</b></td>
          <td>recursive force</td>
          <td>removes the given folder and all of its contents</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div data-id="setup-configuration">
    <h2>Configuration</h2>

    <div class="info">
      <b>git config</b> is used to set configuration values.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:25%">
        <col style="width:75%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git config --global</b></td>
          <td>
            Sets a configuration value for all repositories of this user instead of just this repo.
            A global setting will be written to the <b>.gitconfig</b> file.
          </td>
        </tr>

        <tr>
          <td><b>git config --local</b></td>
          <td>
            (default) Sets a configuration value only for this specific repository.
            A local setting will be written to <b>.git/config</b>.
            This requires you to be inside a repository to use.
          </td>
        </tr>

        <tr>
          <td><b>git config --system</b></td>
          <td>
            Sets a configuration value for all users on the machine and all repositories of those users.
            A system setting will be written to the system <b>.gitconfig</b> file.
          </td>
        </tr>

        <tr>
          <td><b>git config user.name</b></td>
          <td>
            Sets or shows the username that will be used on commits.
          </td>
        </tr>

        <tr>
          <td><b>git config user.email</b></td>
          <td>
            Sets or shows the email address that will be used on commits.
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Set username and email globally (applies to all repositories)
  git config --global user.name "Your Name"
  git config --global user.email "you@example.com"

  # Show the currently configured username and email (local or global)
  git config user.name
  git config user.email

  # Set username and email only for the current repository
  git config --local user.name "Repo Specific Name"
  git config --local user.email "repo@example.com"

  # Set username and email system-wide (for all users on the machine)
  git config --system user.name "System User"
  git config --system user.email "system@example.com"
    </code></pre>
  </div>

  <div data-id="setup-vs code as editor">
    <h2>VS Code as editor</h2>

    <div class="info">
      The <b>--wait</b> makes the terminal wait to execute the commit until the editor page is closed.<br>
      The <b>git mergetool</b> command will show the conflicts in files in the preconfigured IDE (VS Code). You can use this command later on when merge conflicts occur on a merge command.
      <br><br>
      See <a href="https://git-scm.com/book/en/v2/Appendix-C:-Git-Commands-Setup-and-Config" target="_blank" rel="noopener noreferrer">git-scm.com book on chapter A3.1 Appendix C: Git Commands - Setup and Config</a>
    </div>

    <pre><code class="language-bash">
  # Configuration steps:

  git config --global core.editor "code --wait"

  # It might be necessary to select 'Shell Command: Install 'code' command in PATH'
  # in VS Code (in 'Show and Run Commands') to make vscode the default editor

  git config --global merge.tool vscode
  git config --global mergetool.vscode.cmd "code --wait $MERGED"
  git config --global diff.tool vscode
  git config --global difftool.vscode.cmd "code --wait --diff $LOCAL $REMOTE"
    </code></pre>
  </div>
</div>


<!-- BASICS -->

<div data-id="git-basics" class="level1-container">
  <h1 class="header">Basics</h1>

  <div data-id="basics-the three areas">
    <h2>The three areas</h2>

    <div class="info">
      Git works with 3 areas:<br>
      <b>WORKING DIRECTORY → STAGING AREA → REPOSITORY (commits)</b><br><br>

      <b>Working directory:</b>
      Your local files while you are working on them<br>

      <b>Staging area (index):</b>
      What is ready to be included in the next commit<br>

      <b>Repository:</b>
      The database containing all commits, branches and history<br><br>

      The repository is always local, the HEAD is always local.<br>
      The local repository can optionally be synchronized with one or more
      REMOTE REPOSITORIES using fetch, pull and push.
    </div>
  </div>

  <div data-id="basics-basic commands">
    <h2>Basic commands</h2>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:22%">
        <col style="width:18%">
        <col style="width:60%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Full</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git -v</b></td>
          <td>--version</td>
          <td>Shows the installed Git version</td>
        </tr>

        <tr>
          <td><b>git clone &lt;url&gt;</b></td>
          <td></td>
          <td>
            Use <b>outside</b> of a Git repository to clone a remote repository locally.
            Creates a new folder in the current directory.
          </td>
        </tr>

        <tr>
          <td><b>git status</b></td>
          <td></td>
          <td>Shows repository status information</td>
        </tr>

        <tr>
          <td><b>git init</b></td>
          <td></td>
          <td>
            Turns the current directory into a Git repository.
            Creates a <b>.git</b> folder.
          </td>
        </tr>

        <tr>
          <td><b>git add file1 file2</b></td>
          <td></td>
          <td>Adds the specified changed files to the staging area</td>
        </tr>

        <tr>
          <td><b>git add folderName</b></td>
          <td></td>
          <td>Adds all changed files in the given folder to the staging area</td>
        </tr>

        <tr>
          <td><b>git add .</b></td>
          <td></td>
          <td>Adds all changed files to the staging area</td>
        </tr>

        <tr>
          <td><b>git commit</b></td>
          <td></td>
          <td>Opens the configured editor to write a commit message and commit</td>
        </tr>

        <tr>
          <td><b>git commit -m</b></td>
          <td>--message</td>
          <td>Commits with the given commit message</td>
        </tr>

        <tr>
          <td><b>git commit -a -m</b></td>
          <td>--all --message</td>
          <td>
            Automatically stages (adds) all changed <b>tracked</b> files
            (does not include new untracked files), then commits
          </td>
        </tr>

        <tr>
          <td><b>git commit -am</b></td>
          <td>--all --message</td>
          <td>Shorthand for <b>git commit -a -m</b></td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Show installed Git version
  git --version
  git -v

  # Clone a remote repository (run outside an existing repo)
  git clone https://github.com/octocat/Hello-World.git

  # Check repository status
  git status

  # Initialize a new Git repository
  git init

  # Stage files and folders
  git add index.html styles/main.css
  git add src/components/
  git add .

  # Commit changes
  git commit
  git commit -m "Add initial layout and base styles"

  # Stage all tracked files and commit
  git commit -a -m "Fix navigation alignment"
  git commit -am "Refactor header component"
    </code></pre>
  </div>

  <div data-id="basics-viewing history">
    <h2>Viewing history</h2>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:22%">
        <col style="width:18%">
        <col style="width:60%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Full</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git log</b></td>
          <td></td>
          <td>Provides a log of commits in the repository</td>
        </tr>

        <tr>
          <td><b>git log --oneline</b></td>
          <td>--pretty=oneline --abbrev-commit</td>
          <td>Shows each commit on a single line</td>
        </tr>

        <tr>
          <td><b>git log --abbrev-commit</b></td>
          <td></td>
          <td>Shows commits with abbreviated commit hashes</td>
        </tr>

        <tr>
          <td><b>git log --oneline --all --graph --decorate</b></td>
          <td>
            --pretty=oneline<br>
            --abbrev-commit --all --graph --decorate
          </td>
          <td>
            Shows all commits of all branches in a graphical, decorated layout
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # View commit history
  git log
  git log --oneline
  git log --abbrev-commit
  git log --oneline --all --graph --decorate
    </code></pre>
  </div>

  <div data-id="basics-amending commits">
    <h2>Amending commits</h2>

    <div class="info">
      <b>git commit --amend</b> replaces the last commit with a new commit that is built from
      the previous commit's content plus whatever is currently staged.
      It opens the editor so you can change the commit message if necessary.
      <br><br>
      If a file isn't staged when you run it, it will <b>not</b> be included.
    </div>

    <pre><code class="language-bash">
  # Fix a commit message you just wrote (opens editor)
  git add index.html styles/main.css
  git commit -m "stuff"
  git commit --amend

  # Add a forgotten file to the last commit
  git add 1.txt 2.txt
  git commit -m "added 3 files"
  git add 3.txt
  git commit --amend

  # Add small fixes to the last commit
  git add styles/main.css
  git commit -m "Add initial layout and base styles"
  git add styles/main.css
  git commit --amend

  # Fix both commit message and content at once
  git add header.css
  git commit -m "layout"
  git add header.css
  git commit --amend

  # Amend the last commit without opening the editor
  git commit --amend -m "Add initial layout and base styles"
    </code></pre>
  </div>
</div>


<!-- BRANCHES AND MERGING -->

<div data-id="git-branches and merging" class="level1-container">
  <h1 class="header">Branches and merging</h1>

  <div data-id="branches and merging-branch commands">
    <h2>Branch commands</h2>

    <div class="info">
      In Git, a branch is basically a name that points to a specific commit in the commit history.<br>
      Each commit has a unique SHA-1 hash.<br>
      A branch is just a ref (reference) that stores the hash of the commit at the tip of the branch.<br><br>

      Example:<br>
      main → points to commit a1b2c3d4<br>
      feature → points to commit f5e6d7c8<br><br>

      Refs are stored in files inside <b>.git/refs/heads/</b> locally, and on the remote refs are stored similarly inside its own .git repository.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:22%">
        <col style="width:18%">
        <col style="width:60%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Full</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git branch</b></td>
          <td></td>
          <td>Shows all local branches and marks the current branch with an *</td>
        </tr>

        <tr>
          <td><b>git branch -a</b></td>
          <td>--all</td>
          <td>Also shows all remote branches</td>
        </tr>

        <tr>
          <td><b>git branch -v</b></td>
          <td>--verbose</td>
          <td>Shows all branches and their last commit</td>
        </tr>

        <tr>
          <td><b>git branch &lt;branchname&gt;</b></td>
          <td></td>
          <td>Creates a new branch from the current HEAD</td>
        </tr>

        <tr>
          <td><b>git checkout &lt;branchname&gt;</b></td>
          <td></td>
          <td>
            (DEPRECATED) Switches to the given branch.
            Has more options than <b>git switch</b> and is the older syntax.
          </td>
        </tr>

        <tr>
          <td><b>git checkout -b &lt;branchname&gt;</b></td>
          <td>--branch</td>
          <td>(DEPRECATED) Creates a new branch and switches to it</td>
        </tr>

        <tr>
          <td><b>git switch &lt;branchname&gt;</b></td>
          <td></td>
          <td>Switches HEAD to the given branch</td>
        </tr>

        <tr>
          <td><b>git switch -c &lt;branchname&gt;</b></td>
          <td>Same as git checkout -b &lt;branchname&gt;</td>
          <td>Creates a new branch and switches to it</td>
        </tr>

        <tr>
          <td><b>git branch -d &lt;branchname&gt;</b></td>
          <td>--delete &lt;branchname&gt;</td>
          <td>
            Deletes the branch if it is not the current one and is fully merged
          </td>
        </tr>

        <tr>
          <td><b>git branch -d --force &lt;branchname&gt;</b></td>
          <td></td>
          <td>Deletes the branch even if it is not fully merged</td>
        </tr>

        <tr>
          <td><b>git branch -D &lt;branchname&gt;</b></td>
          <td>-d --force &lt;branchname&gt;</td>
          <td>Shorthand for forced branch deletion</td>
        </tr>

        <tr>
          <td><b>git branch -m &lt;newname&gt;</b></td>
          <td>--move &lt;newname&gt;</td>
          <td>Renames the current branch</td>
        </tr>

        <tr>
          <td><b>git branch -M &lt;newname&gt;</b></td>
          <td>--move --force &lt;newname&gt;</td>
          <td>
            Forcefully renames the current branch,
            even if the target branch name already exists
          </td>
        </tr>

        <tr>
          <td><b>git branch -r</b></td>
          <td></td>
          <td>Shows the remote branches known by the local repository</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Show local and remote branches
  git branch
  git branch -a # Also shows all remote branches
  git branch -r # Shows the remote branches known by the local repository
  git branch -v # Shows all branches and their last commit

  # Create new branches
  git branch feature/login
  git branch bugfix/navbar-overflow

  # Switch between branches
  git switch main
  git switch feature/login

  # Create and switch to a new branch (modern way)
  git switch -c feature/profile-page

  # Create and switch to a new branch (legacy way)
  git checkout -b feature/settings-page

  # Rename the current branch
  git branch -m feature/authentication
  git branch -M main

  # Delete branches
  git branch -d feature/old-experiment
  git branch -D bugfix/broken-layout
    </code></pre>
  </div>

  <div data-id="branches and merging-merging">
    <h2>Merging</h2>

    <div class="info">
      <b>git merge &lt;branchname&gt;</b> merges the given branch into the branch you are currently on (HEAD).<br><br>

      Branches are merged, not commits.<br>
      Merges always happen into the current HEAD, so HEAD must be on the <b>receiving</b> branch.
      To merge into master, master must be HEAD.
    </div>

    <div class="info">
      A merge does not always create a commit:
      <ul>
        <li>A <b>fast forward merge</b> does not create a commit because it does not need one</li>
        <li>A merge <b>without conflicts</b> automatically creates a merge commit</li>
        <li>A merge <b>with conflicts</b> requires a manual staging and commit</li>
      </ul>
    </div>

    <div class="info">
      <b>Fast forward merge</b>: happens when there have been no new commits on the receiving branch since the feature branch was created (or last diverged). Merging just moves the pointer forward.<br><br>

      Example:
      <ol>
        <li>Make a feature branch from master and switch to it (both point to the same commit)</li>
        <li>Make changes on the feature branch and commit (feature ahead of master)</li>
        <li>Switch back to master and merge (no conflict, effect is as if changes were made on master)</li>
      </ol>

      <b>Non-fast forward merge without conflicts</b>: both branches have changes, but they do not conflict (e.g. different files). Creates a merge commit with two parents<br>
      <b>Non-fast forward merge with conflicts</b>: both branches have changes and they conflict (e.g. same line). Requires manual resolution and then commit
    </div>

    <pre><code class="language-bash">
  # Merge a branch into the current branch
  git switch main
  git merge feature/login
    </code></pre>
  </div>

  <div data-id="branches and merging-merge conflicts">
    <h2>Merge conflicts</h2>

    <div class="info">
      After resolving merge conflicts, you still have to stage and commit the merged changes.<br><br>

      When VS Code (or another IDE) has been set up as the mergetool, <b>git mergetool</b> opens the
      conflicting files directly in the IDE.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git mergetool</b></td>
          <td>Opens the conflicting files in the preconfigured IDE (e.g. VS Code)</td>
        </tr>
        <tr>
          <td><b>code .</b></td>
          <td>
            Opens the current folder in VS Code.
            Useful for inspecting files during merge conflicts.
          </td>
        </tr>
        <tr>
          <td><b>code &lt;filename&gt;</b></td>
          <td>Opens the given file in VS Code</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Inspect files during a merge conflict
  git mergetool
  code .
  code src/components/Header.js

  # After resolving the conflicts, stage and commit
  git add src/components/Header.js
  git commit -m "Resolve merge conflict in Header"
    </code></pre>
  </div>
</div>


<!-- DIFF -->

<div data-id="git-diff" class="level1-container">
  <h1 class="header">Diff</h1>

  <div data-id="diff-reading a diff">
    <h2>Reading a diff</h2>

    <div class="info">
      The <b>diff</b> command shows differences between various areas (Working Directory, Staging, HEAD) and between branches/commits.
      <br><br>
      <b>git diff</b>: shows all the changes in the working directory (current branch) that are not staged yet<br>
      [WORKING DIR VS STAGING]
    </div>

    <pre><code class="language-diff">
    git diff

    # Comparing staged version (a) with working directory version (b)
    diff --git a/index.html b/index.html
    index e69de29..4b825dc 100644

    --- a/index.html   # lines present in file 'a' (staging area) that are not present in file 'b'
    +++ b/index.html   # lines present in file 'b' (working directory) that are not present in file 'a'

    @@ -1,3 +1,4 @@   # -1,3 → lines from file 'a' (start at line 1, show 3 lines)
                      # +1,4 → lines from file 'b' (start at line 1, show 4 lines)
    &lt;header&gt;
    -  &lt;h1&gt;Welcome&lt;/h1&gt;  # this line exists in 'a' but was removed in 'b'
    +  &lt;h1&gt;Welcome back&lt;/h1&gt;  # this line exists in 'b' but not in 'a'
    +  &lt;p&gt;User dashboard loaded&lt;/p&gt;  # this line exists in 'b' but not in 'a'
    &lt;/header&gt;
    </code></pre>

    <div class="info">
      When VS Code (or another IDE) has been set up as the difftool, you can run every diff command using
      <b>git difftool</b> instead of <b>git diff</b> to open VS Code and view the differences
      (e.g. <b>git difftool HEAD</b>).
    </div>
  </div>

  <div data-id="diff-diff commands">
    <h2>Diff commands</h2>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git diff</b></td>
          <td>
            Shows differences between the working directory and the staging area.
            Only unstaged changes are shown.<br>
            [WORKING DIR VS STAGING]
          </td>
        </tr>

        <tr>
          <td><b>git diff &lt;file&gt;</b></td>
          <td>
            Shows differences for a specific file between the working directory
            and the staging area (if the file is staged).<br>
            Only unstaged changes in that file are shown.<br>
            [WORKING DIR VS STAGING]
          </td>
        </tr>

        <tr>
          <td><b>git diff --staged</b></td>
          <td>
            Shows differences between the staging area and HEAD (last commit).
            Only staged changes are shown.<br>
            Also known as <b>git diff --cached</b> (the staging area used to be called cache).<br>
            [STAGING VS HEAD]
          </td>
        </tr>

        <tr>
          <td><b>git diff &lt;file&gt; --staged</b></td>
          <td>
            Shows staged changes for a specific file only,
            comparing the staging area with HEAD (last commit).<br>
            [STAGING VS HEAD]
          </td>
        </tr>

        <tr>
          <td><b>git diff HEAD</b></td>
          <td>
            Shows differences between the working directory plus staging area
            and HEAD (last commit).<br>
            Includes both staged and unstaged changes.<br>
            [WORKING DIR + STAGING VS HEAD]
          </td>
        </tr>

        <tr>
          <td><b>git diff HEAD~&lt;n&gt;</b></td>
          <td>
            Shows differences between the working directory plus staging area
            and the nth commit before HEAD.
          </td>
        </tr>

        <tr>
          <td><b>git diff HEAD &lt;file&gt;</b></td>
          <td>
            Shows differences between the working directory version of a file
            and the version of that file in HEAD (last commit).<br>
            Staging area is ignored for this comparison.<br>
            [WORKING DIR VS HEAD]
          </td>
        </tr>

        <tr>
          <td><b>git diff &lt;branch&gt;</b></td>
          <td>
            Shows the difference between the current branch (HEAD)
            and another local branch.<br>
            The current branch is shown as file <b>a</b>,
            the specified branch as file <b>b</b>.
          </td>
        </tr>

        <tr>
          <td><b>git diff &lt;remote&gt;/&lt;branch&gt;</b></td>
          <td>
            Shows the difference between the current branch (HEAD)
            and a remote-tracking branch.<br>
            The current branch is shown as file <b>a</b>,
            the remote-tracking branch as file <b>b</b>.
          </td>
        </tr>

        <tr>
          <td><b>git diff branch1..branch2</b></td>
          <td>
            Compares the last commit of branch1 with the last commit of branch2.
            branch1 is shown as file <b>a</b>, branch2 as file <b>b</b>.<br>
            <b>git diff branch1 branch2</b> (with a space) does the same.
          </td>
        </tr>

        <tr>
          <td><b>git diff hash1..hash2</b></td>
          <td>
            Compares commit hash1 with commit hash2.
            Hashes can be obtained via <b>git log --oneline</b>.<br>
            <b>git diff hash1 hash2</b> (with a space) does the same.
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Unstaged changes
  git diff
  git diff src/app.js

  # Staged changes
  git diff --staged
  git diff --cached

  # Everything since the last commit
  git diff HEAD
  git diff HEAD~2
  git diff HEAD src/app.js

  # Compare branches
  git diff main
  git diff origin/main
  git diff main..feature/login

  # Compare commits
  git diff a1b2c3d..e4f5g6h
    </code></pre>
  </div>
</div>


<!-- STASH -->

<div data-id="git-stash" class="level1-container">
  <h1 class="header">Stash</h1>

  <div data-id="stash-stash commands">
    <h2>Stash commands</h2>

    <div class="info">
      Stash removes changes from the working directory (reverts it to the last commit) and saves them temporarily
      when you are not ready to commit (for example when you want to switch branches).
      <br><br>
      Applying stashed changes can result in merge conflicts; these are handled like normal merge conflicts.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git stash</b></td>
          <td>Stashes the current uncommitted changes from the working directory.</td>
        </tr>

        <tr>
          <td><b>git stash save</b></td>
          <td>Same as <b>git stash</b> (older, explicit syntax).</td>
        </tr>

        <tr>
          <td><b>git stash pop</b></td>
          <td>Applies the most recent stash to the working directory and removes it from the stash list.</td>
        </tr>

        <tr>
          <td><b>git stash apply</b></td>
          <td>Applies the most recent stash but keeps it in the stash list.</td>
        </tr>

        <tr>
          <td><b>git stash list</b></td>
          <td>
            Shows all stashed changes with their stash identifiers (indexes).<br>
            Note that every time you stash something the previous indexes just get +1, the most current stash is always index 0.
          </td>
        </tr>

        <tr>
          <td><b>git stash apply stash@{x}</b></td>
          <td>Applies a specific stash identified by its index.</td>
        </tr>

        <tr>
          <td><b>git stash drop stash@{x}</b></td>
          <td>Removes a specific stash from the stash list.</td>
        </tr>

        <tr>
          <td><b>git stash clear</b></td>
          <td>Removes all stashes permanently.</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Temporarily store uncommitted changes
  git stash

  # Explicit (older) syntax
  git stash save

  # View all stashed changes
  git stash list

  # Apply the most recent stash and remove it from the list
  git stash pop

  # Apply the most recent stash but keep it for later reuse
  git stash apply

  # Apply a specific stash by index
  git stash apply stash@{1}

  # Remove a specific stash
  git stash drop stash@{1}

  # Remove all stashes
  git stash clear
    </code></pre>
  </div>
</div>


<!-- UNDOING CHANGES -->

<div data-id="git-undoing changes" class="level1-container">
  <h1 class="header">Undoing changes</h1>

  <div data-id="undoing changes-viewing old commits">
    <h2>Viewing old commits</h2>

    <div class="info">
      <b>git checkout &lt;commit-hash&gt;</b>: views a previous commit (only first 7 digits needed). This moves you into a <b>detached HEAD</b> state.<br><br>

      In detached HEAD state you can:<br>
      - View files<br>
      - Leave and go back to where you were before or to another branch (reattach HEAD)<br>
      - Create a new branch and switch to it to make and save changes
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git checkout &lt;commit-hash&gt;</b></td>
          <td>
            Switches to a specific commit by its hash (using git log).
            This puts you in a <b>detached HEAD</b> state.
          </td>
        </tr>

        <tr>
          <td><b>git checkout HEAD~x</b></td>
          <td>
            Switches to the xth parent commit before HEAD.
            Also results in a <b>detached HEAD</b> state.
          </td>
        </tr>

        <tr>
          <td><b>git switch -</b></td>
          <td>
            Switches back to the previous branch you were on.
            Useful for quick toggling between two branches.
          </td>
        </tr>

        <tr>
          <td><b>git checkout &lt;remote&gt;/&lt;branch&gt;</b></td>
          <td>
            Checks out a remote-tracking branch commit in a detached HEAD state
            (e.g. <b>origin/main</b>).
            Useful to inspect the remote branch without creating a local branch.
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # View a specific commit (detached HEAD)
  git checkout a1b2c3d

  # View the commit one before the current HEAD
  git checkout HEAD~1

  # Quickly switch back to the previous branch
  git switch -

  # Inspect the remote main branch without creating a local branch
  git checkout origin/main
    </code></pre>
  </div>

  <div data-id="undoing changes-revert vs reset vs restore">
    <h2>Revert vs reset vs restore</h2>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:28%">
        <col style="width:24%">
        <col style="width:24%">
        <col style="width:24%">
      </colgroup>
      <thead>
        <tr>
          <th></th>
          <th>revert</th>
          <th>reset</th>
          <th>restore</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>When to use</b></td>
          <td>Undo a commit that is already shared</td>
          <td>Rewrite or remove local commits (not shared)</td>
          <td>Undo local file or staging changes</td>
        </tr>

        <tr>
          <td><b>Affects</b></td>
          <td>Commit history (by adding a commit)</td>
          <td>HEAD / Staging / Working directory</td>
          <td>Files / Staging</td>
        </tr>

        <tr>
          <td><b>Changes existing commits?</b></td>
          <td>No</td>
          <td>Yes</td>
          <td>No</td>
        </tr>

        <tr>
          <td><b>Creates a new commit?</b></td>
          <td>Yes</td>
          <td>No</td>
          <td>No</td>
        </tr>

        <tr>
          <td><b>Safe on shared branches?</b></td>
          <td>Yes</td>
          <td>No</td>
          <td>Yes</td>
        </tr>
      </tbody>
    </table>

    <div class="info">
      If you want to reverse commits that other people already have on their machines, use <b>revert</b>.
      If you want to reverse commits that you haven't shared with others, use <b>reset</b> and no one will ever know.
    </div>
  </div>

  <div data-id="undoing changes-revert">
    <h2>Revert</h2>

    <div class="info">
      <b>git revert</b> undoes a previous commit by creating a new commit that restores the code to the state it had before that commit.<br><br>
      - It does not modify or remove existing commits<br>
      - The original commit stays in the history<br>
      - A new commit is added that reverses the changes<br>
      - This makes it safe to use on shared branches<br>
      - git revert operates on commits, not on individual files or local working directory changes<br>
      - git revert can cause conflicts that have to be resolved, just like any other conflict<br>
      <br>Use git revert when:<br>
      - A commit has already been pushed<br>
      - Other people may have pulled that commit<br>
      - You want to undo changes without rewriting history<br>
    </div>

    <pre><code class="language-bash">
  # Revert a specific commit by its hash
  git revert a1b2c3d

  # Revert the most recent commit
  git revert HEAD

  # Revert the commit before the last one
  git revert HEAD~1

  # Revert a commit that is a few commits back
  git revert HEAD~3
    </code></pre>
  </div>

  <div data-id="undoing changes-reset">
    <h2>Reset</h2>

    <div class="info">
      <b>git reset</b> undoes commits by moving the current branch pointer (HEAD) to an earlier commit.<br><br>

      - It can remove commits from the current branch<br>
      - Depending on the mode, it may also modify the staging area and working directory<br>
      - Commits after the reset point are no longer part of the branch history<br>
      - This rewrites history and can permanently discard work<br>
      - For this reason, it is <b>not safe</b> to use on shared branches<br>
      - git reset operates on commits and pointers, not on individual files only<br>

      <br>Use git reset when:<br>
      - The commits have <b>not been pushed</b> yet<br>
      - You are working on a local branch only (no commits for the branch pushed yet)<br>
      - You want to edit, squash, or remove recent commits<br>
      - You understand which reset mode you are using<br>
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git reset (--mixed) &lt;target&gt;</b></td>
          <td>
            Resets the branch back to the target commit (commits after it are no longer part of the branch history).<br>
            <b>--mixed</b> is default: resets <b>HEAD</b> + <b>staging</b>, keeps <b>working directory</b> (changes remain as unstaged).
          </td>
        </tr>

        <tr>
          <td><b>git reset --soft &lt;target&gt;</b></td>
          <td>
            Resets only <b>HEAD</b>; keeps <b>staging</b> + <b>working directory</b> (changes stay staged).
            Useful to redo the last commit without losing work.
          </td>
        </tr>

        <tr>
          <td><b>git reset --hard &lt;target&gt;</b></td>
          <td>
            Resets <b>HEAD</b> + <b>staging</b> + <b>working directory</b> to match the target commit.
            Discards local changes (e.g. <b>git reset --hard HEAD~1</b> removes the last commit and its changes).
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Move HEAD back by one commit, keep changes staged
  git reset --soft HEAD~1

  # Move HEAD back by one commit, keep changes in working directory
  git reset HEAD~1

  # Move HEAD back by one commit and discard all changes
  git reset --hard HEAD~1

  # Reset the branch to a specific commit hash
  git reset --hard a1b2c3d
    </code></pre>

    <div class="info">
      A commit removed with <b>git reset --hard</b> is not necessarily gone forever.
      See <b>Reflog</b> for how to get it back.
    </div>
  </div>

  <div data-id="undoing changes-restore">
    <h2>Restore</h2>

    <div class="info">
      <b>git restore</b> is used to undo local changes in your working directory or staging area by restoring files to a previous state (usually <b>HEAD</b>).<br><br>

      - It operates on <b>files</b>, not on commits<br>
      - It can discard changes in the working directory (uncommitted changes)<br>
      - It can unstage files (remove them from the staging area) while keeping the changes in the working directory<br>
      - By default, it restores files from <b>HEAD</b> (last commit on the current branch)<br>
      - It does not rewrite commit history and is safe to use on shared branches<br>
      - Legacy equivalents exist via <b>git checkout</b> for restoring files (older syntax)<br>

      <br>Use git restore when:<br>
      - You want to discard local changes to one or more files<br>
      - You accidentally staged a file and want to unstage it<br>
      - You want to restore a file from an older commit without switching branches<br>
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git restore &lt;filename(s)&gt;</b></td>
          <td>
            Discards ALL local (unstaged) changes in the file(s) by restoring them from <b>HEAD</b>.
            HEAD is the default source option.
          </td>
        </tr>

        <tr>
          <td><b>git restore .</b></td>
          <td>
            Same as above but for all files in the current directory (and subdirectories):
            discards ALL local (unstaged) changes by restoring from <b>HEAD</b>.
          </td>
        </tr>

        <tr>
          <td><b>git restore --staged &lt;filename(s)&gt;</b></td>
          <td>
            Unstages the file(s) (removes them from the staging area) while keeping the changes in the working directory.
          </td>
        </tr>

        <tr>
          <td><b>git restore --source &lt;commithash/HEAD~x&gt; file.js</b></td>
          <td>
            Restores the contents of <b>file.js</b> to the state of the xth commit before current HEAD,
            or to a given commit hash, without switching branches.
          </td>
        </tr>

        <tr>
          <td><b>git checkout HEAD &lt;filename(s)&gt;</b></td>
          <td>
            Legacy syntax: discards ALL changes in the file(s) and restores them back to <b>HEAD</b>
            (same result as <b>git restore &lt;filename(s)&gt;</b>).
          </td>
        </tr>

        <tr>
          <td><b>git checkout -- &lt;filename(s)&gt;</b></td>
          <td>
            Legacy shorthand for <b>git checkout HEAD &lt;filename(s)&gt;</b>.
            Discards ALL changes in the file(s) and restores them back to <b>HEAD</b>.
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Discard ALL local changes in specific files (restore from HEAD)
  git restore src/app.js src/styles/main.css

  # Discard ALL local changes in all files in the current directory
  git restore .

  # Unstage files but keep their changes in the working directory
  git restore --staged src/app.js

  # Restore a file from an older commit into the working directory
  git restore --source HEAD~2 src/index.html

  # Legacy syntax (same effect as git restore)
  git checkout HEAD src/app.js
  git checkout -- src/app.js
    </code></pre>
  </div>
</div>


<!-- REMOTES -->

<div data-id="git-remotes" class="level1-container">
  <h1 class="header">Remotes</h1>

  <div data-id="remotes-connecting a remote">
    <h2>Connecting a remote</h2>

    <div class="info">
      <b>git clone &lt;url&gt;</b>: retrieves all files associated with the repo and copies them into a new folder locally.<br>
      It also initializes the local repo (git init).<br>
      Make sure you are <b>not</b> in a repo when cloning.<br><br>

      SSH key setup documentation: <a href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh" target="_blank" rel="noopener noreferrer">here</a>
    </div>

    <div class="info">
      There are two ways to link a local repo to a remote:<br><br>
      <b>Option 1</b>: Create the repo on GitHub first, then clone it locally and start your work from there. Git will automatically configure/link the remote.<br>
      <b>Option 2</b>: Start your work in a local repo first. Then create a remote repo and connect them using <b>git remote add</b>.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git remote -v</b></td>
          <td>(--verbose) Shows existing remotes</td>
        </tr>
        <tr>
          <td><b>git remote add &lt;name&gt; &lt;url&gt;</b></td>
          <td>
            Adds a remote with the given alias. The most common/conventional name is <b>origin</b>.
            Use the SSH URL if you want to make use of your SSH key.
          </td>
        </tr>
        <tr>
          <td><b>git remote rename &lt;old&gt; &lt;new&gt;</b></td>
          <td>Renames a remote (e.g. origin to debron)</td>
        </tr>
        <tr>
          <td><b>git remote remove &lt;name&gt;</b></td>
          <td>Deletes a remote</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  git clone git@github.com:MatthiasSw/autumn8.git

  git remote -v
  git remote add origin git@github.com:MatthiasSw/autumn8.git
  git remote rename origin debron
  git remote remove debron
    </code></pre>
  </div>

  <div data-id="remotes-pushing fetching and pulling">
    <h2>Pushing, fetching and pulling</h2>

    <div class="info">
      Commits don't get pushed; an entire <b>branch</b> gets pushed.<br><br>
      You cannot push to a remote branch unless your local branch is up to date with that remote branch.
      In a centralized workflow this often means pulling before pushing, which can lead to merge conflicts if changes overlap.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:32%">
        <col style="width:68%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git push &lt;remote&gt; &lt;branch&gt;</b></td>
          <td>
            Pushes the given local branch to the given remote, onto a remote branch with the same name.
            You don't need to be on the branch you're pushing.
            If the branch does not exist yet, it will be created on the remote.
          </td>
        </tr>

        <tr>
          <td><b>git push &lt;remote&gt; &lt;local&gt;:&lt;remote-branch&gt;</b></td>
          <td>
            Pushes the given local branch to a specific (differently named) remote branch.<br>
            e.g. <b>git push origin myLocalBranch:myRemoteBranch</b>
          </td>
        </tr>

        <tr>
          <td><b>git push -u &lt;remote&gt; &lt;branch&gt;</b></td>
          <td>
            (--set-upstream) Same as git push, but links the remote branch to the local one,
            so afterwards you can just use <b>git push</b>.
          </td>
        </tr>

        <tr>
          <td><b>git switch &lt;remotebranchname&gt;</b></td>
          <td>
            After cloning you don't automatically have all remote branches locally.
            git switch creates the branch locally and sets the upstream automatically.<br>
            NOTE: git switch is made for LOCAL branches. To only view a remote branch without creating a
            local branch, use <b>git checkout &lt;remote&gt;/&lt;branch&gt;</b> (detached HEAD).
          </td>
        </tr>

        <tr>
          <td><b>git checkout --track &lt;remote&gt;/&lt;branch&gt;</b></td>
          <td>
            Same as <b>git switch &lt;remotebranchname&gt;</b>, older syntax.
            It differs from <b>git checkout &lt;remote&gt;/&lt;branch&gt;</b> in that it does create the local
            branch instead of only viewing it.
          </td>
        </tr>

        <tr>
          <td><b>git fetch &lt;remote&gt;</b></td>
          <td>
            (default remote is origin) Fetches branches and history from a remote repo.
            Only updates the remote-tracking branches in the local repo; it does not touch your working directory.
          </td>
        </tr>

        <tr>
          <td><b>git fetch &lt;remote&gt; &lt;branch&gt;</b></td>
          <td>Fetches a specific remote branch and updates only that remote-tracking branch.</td>
        </tr>

        <tr>
          <td><b>git merge &lt;remote&gt;/&lt;branch&gt;</b></td>
          <td>Merges a fetched remote-tracking branch into your current branch.</td>
        </tr>

        <tr>
          <td><b>git pull &lt;remote&gt; &lt;branch&gt;</b></td>
          <td>
            git fetch + git merge. It updates the remote-tracking branch and then merges it into your current branch.
            It matters <b>where</b> you run it from: the current branch receives the merge.
            Beware: pull can produce merge conflicts, handled like any merge conflict.
          </td>
        </tr>

        <tr>
          <td><b>git pull</b></td>
          <td>
            Shorthand for fetch + merge. Defaults to <b>origin</b> and to the remote branch
            with the same name as your current branch.
          </td>
        </tr>

        <tr>
          <td><b>git push &lt;remote&gt; --delete &lt;branch&gt;</b></td>
          <td>
            Deletes the branch on the remote. Git treats branches as refs;
            this effectively pushes nothing to that ref, which deletes it.
          </td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Pushing
  git push origin main
  git push origin myLocalBranch:myRemoteBranch
  git push -u origin main
  git push

  # Getting a remote branch locally
  git switch myremotebranch
  git checkout --track origin/myremotebranch

  # Fetching (updates remote-tracking branches only)
  git fetch
  git fetch origin
  git fetch origin main

  # Inspect and merge a fetched branch
  git checkout origin/main
  git merge origin/main

  # Pulling (fetch + merge)
  git pull
  git pull origin main

  # Delete a branch on the remote
  git push origin --delete myremotebranch
    </code></pre>

    <div class="info">
      GitHub's default branch name is <b>main</b>, Git's default is <b>master</b>.
      It can be a good idea to rename either locally or remotely depending on the situation.
      If a GitHub repo is empty (no branches yet), you can define the default branch name in the first push
      (<b>git push origin master</b> or <b>git push origin main</b>).
    </div>
  </div>

  <div data-id="remotes-forking">
    <h2>Forking</h2>

    <div class="info">
      A <b>fork</b> is used to make contributions to an external repo that you don't have any rights on.<br><br>

      You can clone any repo, but you can't push to it if you don't have rights.
      And if you can't push, you can't submit a pull request.
      So you fork the repo: that way you can push to <b>your own</b> fork and submit a PR from your fork
      to the original repo.
    </div>

    <div class="info">
      <b>The flow:</b>
      <ol>
        <li>Fork the original repo on GitHub. It becomes available in your own GitHub account.</li>
        <li>Clone <b>your fork</b> locally. The <b>origin</b> remote will point to your fork.</li>
        <li>
          Changes will keep happening on the original repo, and you will want those too.
          So add a second remote pointing to the original repo to pull down any changes/updates.
          Conventionally this remote is named <b>upstream</b> (or <b>original</b>).
        </li>
        <li>Do your work on a branch of your fork, push it to your fork, then open a PR to the original repo.</li>
      </ol>
    </div>

    <pre><code class="language-bash">
  # 1 + 2. Fork on GitHub, then clone YOUR fork
  git clone git@github.com:YourName/autumn8.git
  cd autumn8

  # 3. Add the original repo as a second remote
  git remote add upstream https://github.com/MatthiasSw/autumn8.git

  # Check both remotes
  git remote -v
  # origin    git@github.com:YourName/autumn8.git       (fetch/push)  -> your fork
  # upstream  https://github.com/MatthiasSw/autumn8.git (fetch/push)  -> the original

  # Keep your fork up to date with the original
  git fetch upstream
  git switch main
  git merge upstream/main

  # 4. Work, push to YOUR fork, then open a PR on GitHub
  git switch -c feature/my-contribution
  git add .
  git commit -m "Add my contribution"
  git push origin feature/my-contribution
    </code></pre>
  </div>
</div>


<!-- REBASING -->

<div data-id="git-rebasing" class="level1-container">
  <h1 class="header">Rebasing</h1>

  <div data-id="rebasing-how rebasing works">
    <h2>How rebasing works</h2>

    <div class="info">
      Rebasing is a Git operation that moves or re-applies a series of commits from one branch onto a
      different base commit, effectively rewriting the commit history to appear as though the work was
      started from a different point in time.
    </div>

    <div class="info">
      When you run <b>git rebase &lt;target&gt;</b>, Git:
      <ol>
        <li>Finds the common ancestor (the base commit) between your current branch and the target</li>
        <li>Temporarily sets aside your commits (as patches)</li>
        <li>Moves your branch pointer to the tip of the target</li>
        <li>Re-applies your commits one by one on top of it</li>
      </ol>
    </div>

    <pre><code class="language-none">
  Before rebase:

    D---E---F---G         main
         \
          A---B---C       feature


  After 'git rebase main':

    D---E---F---G         main
                 \
                  A'--B'--C'   feature
    </code></pre>

    <div class="info">
      The commits A, B, C become A', B', C' — they contain the <b>same changes</b> but have
      <b>new commit hashes</b>, because their parent has changed.
    </div>

    <h3>Parent commits and the base commit</h3>

    <div class="info">
      Every commit has a <b>parent</b>. A commit isn't just a snapshot — it also points to the commit
      that came before it. This is how commits form a chain:
    </div>

    <pre><code class="language-none">
    A---B---C---D

    # C is the parent of D, B is the parent of C, and so on.
    </code></pre>

    <div class="info">
      The <b>base commit</b> is the starting point of your branch.
      When you create a new branch, you always do so from an existing commit.
      That commit is the base — the point where your branch splits off.
    </div>

    <pre><code class="language-none">
    D---E---F---G         main
         \
          A---B---C       feature

    # E is the base commit of feature. It's where the two branches diverged.
    </code></pre>

    <div class="info">
      <b>Rebasing = choosing a new starting point.</b><br>
      With <b>git rebase main</b> you're saying: "act as if my branch didn't start at E, but at G".<br><br>
      A, B and C are re-applied on top of G. The base has shifted from E to G — hence the name <b>re-base</b>.<br><br>
      In short: the base commit is simply the commit your branch is built on.
      Rebasing is nothing more than moving that foundation to a more recent point.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:30%">
        <col style="width:70%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git rebase &lt;targetbranch&gt;</b></td>
          <td>Re-applies the commits of your current branch on top of the given target branch</td>
        </tr>
        <tr>
          <td><b>git rebase --abort</b></td>
          <td>When merge conflicts arise while rebasing, you can abort the whole thing</td>
        </tr>
        <tr>
          <td><b>git rebase --continue</b></td>
          <td>Continues the rebase after you have resolved and staged the conflicting files</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Re-apply the commits of feature on top of main
  git switch feature
  git rebase main

  # Conflicts? Resolve them, stage them, then continue
  git add src/app.js
  git rebase --continue

  # Or bail out entirely
  git rebase --abort
    </code></pre>
  </div>

  <div data-id="rebasing-merge vs rebase">
    <h2>Merge vs rebase</h2>

    <div class="info">
      Merge and rebase both integrate changes from one branch into another, but they do it differently.
      A merge creates a new "merge commit" that ties two histories together.
      A rebase pretends the branch was always based on the target — no merge commit, just a clean line.
    </div>

    <pre><code class="language-none">
  Starting situation

    D---E---F---G         main
         \
          A---B---C       feature
    </code></pre>

    <h3>Scenario 1: git merge feature (while on main)</h3>

    <pre><code class="language-none">
    D---E---F---G---M     main
         \         /
          A---B---C       feature
    </code></pre>

    <div class="info">
      Git creates a new merge commit <b>M</b> that ties the two histories together.
      Both lines of work are preserved exactly as they happened.
    </div>

    <pre><code class="language-bash">
  git switch main
  git merge feature
    </code></pre>

    <h3>Scenario 2: git rebase main (while on feature)</h3>

    <pre><code class="language-none">
    D---E---F---G         main
                 \
                  A'--B'--C'   feature
    </code></pre>

    <div class="info">
      No merge commit. The commits are re-applied on top of G, as if the feature branch was always
      based on the latest version of main. The history looks linear.
    </div>

    <pre><code class="language-bash">
  git switch feature
  git rebase main
    </code></pre>

    <div class="info">
      After a rebase of your feature branch you can merge it back into main.
      Because the base is now the tip of main, this becomes a fast forward merge:
    </div>

    <pre><code class="language-none">
    D---E---F---G---A'--B'--C'    main
    </code></pre>

    <pre><code class="language-bash">
  git switch main
  git merge feature
    </code></pre>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:34%">
        <col style="width:33%">
        <col style="width:33%">
      </colgroup>
      <thead>
        <tr>
          <th></th>
          <th>Merge</th>
          <th>Rebase</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>History</b></td>
          <td>Preserves original timeline</td>
          <td>Rewrites as linear history</td>
        </tr>
        <tr>
          <td><b>Merge commit</b></td>
          <td>Yes</td>
          <td>No</td>
        </tr>
        <tr>
          <td><b>Traceability</b></td>
          <td>Clear when branches diverged</td>
          <td>Cleaner, but less transparent</td>
        </tr>
        <tr>
          <td><b>Safety</b></td>
          <td>Safe on shared branches</td>
          <td>Risky on shared branches</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div data-id="rebasing-interactive rebase">
    <h2>Interactive rebase</h2>

    <div class="info">
      <b>git rebase -i &lt;target&gt;</b> opens an editor listing your commits, letting you rewrite them.
      This makes interactive rebase a powerful tool for cleaning up history before merging —
      collapsing work-in-progress commits, fixing typos in messages, or reordering changes.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:25%">
        <col style="width:75%">
      </colgroup>
      <thead>
        <tr>
          <th>Action</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>pick</b></td>
          <td>Keep the commit as-is</td>
        </tr>
        <tr>
          <td><b>reword</b></td>
          <td>Change the commit message</td>
        </tr>
        <tr>
          <td><b>squash</b></td>
          <td>Combine the commit into the previous one, keeping both messages</td>
        </tr>
        <tr>
          <td><b>fixup</b></td>
          <td>Combine the commit into the previous one, discarding its message</td>
        </tr>
        <tr>
          <td><b>edit</b></td>
          <td>Pause the rebase so you can amend the commit</td>
        </tr>
        <tr>
          <td><b>drop</b></td>
          <td>Delete the commit entirely</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Clean up the last 3 commits before opening a PR
  git rebase -i HEAD~3

  # Or rebase interactively onto another branch
  git rebase -i main
    </code></pre>

    <pre><code class="language-none">
  # The editor that opens looks like this:

  pick   a1b2c3d Add login form
  squash e4f5g6h WIP
  squash h7i8j9k WIP fix typo
  reword l0m1n2o Add validaton

  # Save and close -> the three WIP commits become one,
  # and you get prompted to fix the "validaton" typo.
    </code></pre>
  </div>

  <div data-id="rebasing-the golden rule">
    <h2>The golden rule</h2>

    <div class="info">
      <b>Never rebase commits that have been pushed to a shared branch.</b><br><br>

      Because rebasing rewrites commit hashes, anyone else who based work on those original commits
      will have a diverged history.<br><br>

      Rebase freely on local or personal feature branches; use merge (or a squash merge) when
      integrating into shared branches like main.
    </div>

    <div class="info">
      Common use cases for rebasing:
      <ul>
        <li>Keeping a feature branch up to date with main without polluting history with merge commits</li>
        <li>Cleaning up messy local commits before opening a pull request</li>
        <li>Squashing a series of small "WIP" commits into one meaningful commit</li>
        <li>Linearizing history to make <b>git log</b> and <b>git bisect</b> easier to navigate</li>
      </ul>
    </div>
  </div>
</div>


<!-- TAGS -->

<div data-id="git-tags" class="level1-container">
  <h1 class="header">Tags</h1>

  <div data-id="tags-tag commands">
    <h2>Tag commands</h2>

    <div class="info">
      A git tag is a <b>pointer to a specific commit</b>. It is normally used for versioning, by which a
      specific commit is tagged to identify the software's version at that point.<br><br>

      There are two kinds: <b>lightweight</b> tags (just a name) and <b>annotated</b> tags
      (with author, date and message). Annotated tags are preferred.
      Multiple tags can be added to a single commit, but tag names must be unique.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:34%">
        <col style="width:66%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git tag</b></td>
          <td>Lists all tags</td>
        </tr>
        <tr>
          <td><b>git tag -l "*searchterm*"</b></td>
          <td>
            Lists all tags matching the search term (supports wildcards).<br>
            e.g. <b>git tag -l "*beta*"</b> lists all tag names containing the word beta
          </td>
        </tr>
        <tr>
          <td><b>git tag &lt;tagname&gt;</b></td>
          <td>Creates a simple (lightweight) tag on the current HEAD</td>
        </tr>
        <tr>
          <td><b>git tag -a &lt;tagname&gt;</b></td>
          <td>
            Creates an annotated tag on the current HEAD.
            The terminal will prompt for a tag message (just like with a commit message)
          </td>
        </tr>
        <tr>
          <td><b>git tag -a &lt;tagname&gt; -m "message"</b></td>
          <td>Creates an annotated tag with the given message</td>
        </tr>
        <tr>
          <td><b>git tag (-a) &lt;tagname&gt; &lt;commithash&gt;</b></td>
          <td>Creates a tag on the given commit instead of on HEAD</td>
        </tr>
        <tr>
          <td><b>git tag (-a) &lt;tagname&gt; (&lt;hash&gt;) -f</b></td>
          <td>
            Moves a tag name that is already in use to the current HEAD or the given commit.
            Tag names must be unique, so moving one requires the <b>-f/--force</b> flag
          </td>
        </tr>
        <tr>
          <td><b>git show &lt;tagname&gt;</b></td>
          <td>Shows the metadata of the annotated tag (tag creator, date, message, etc.)</td>
        </tr>
        <tr>
          <td><b>git checkout &lt;tagname&gt;</b></td>
          <td>Goes to the tagged commit in <b>detached HEAD</b> state</td>
        </tr>
        <tr>
          <td><b>git diff &lt;tag1&gt; &lt;tag2&gt;</b></td>
          <td>Shows the diff between these two tagged commits</td>
        </tr>
        <tr>
          <td><b>git tag -d &lt;tagname&gt;</b></td>
          <td>Deletes the tag with the given name</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # List tags
  git tag
  git tag -l "*beta*"

  # Create tags
  git tag v1.0.0
  git tag -a v1.0.0
  git tag -a v1.0.0 -m "First stable release"
  git tag -a v0.9.0 a1b2c3d

  # Move an existing tag to HEAD
  git tag -a v1.0.0 -f

  # Inspect
  git show v1.0.0
  git diff v0.9.0 v1.0.0

  # Delete
  git tag -d v0.9.0
    </code></pre>

    <div class="info">
      Since new branches are always created from the current HEAD, you can create a new branch starting
      from a tag point after checking out that tag:
    </div>

    <pre><code class="language-bash">
  git checkout v1.0.0        # detached HEAD at the tagged commit
  git switch -c branch_from_tag
    </code></pre>
  </div>

  <div data-id="tags-pushing tags">
    <h2>Pushing tags</h2>

    <div class="info">
      Tags are <b>not pushed to remotes by default</b>.<br><br>

      You will still need to push your commit itself separately. If you push the tag before the commit,
      the tag (and its commit) will be stored on the server, but will only be accessible through the tag
      name since there is no specific branch/commit update to link it to.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:34%">
        <col style="width:66%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git push &lt;remote&gt; &lt;tagname&gt;</b></td>
          <td>Pushes the given tag to the remote</td>
        </tr>
        <tr>
          <td><b>git push &lt;remote&gt; --tags</b></td>
          <td>Pushes all tags to the remote</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Push the branch first, then the tag
  git push origin main
  git push origin v1.0.0

  # Or push all tags at once
  git push origin --tags
    </code></pre>
  </div>
</div>


<!-- REFLOG -->

<div data-id="git-reflog" class="level1-container">
  <h1 class="header">Reflog</h1>

  <div data-id="reflog-reflog commands">
    <h2>Reflog commands</h2>

    <div class="info">
      All actions in git are logged under the <b>.git/logs</b> directory.<br><br>

      <b>git log</b>: shows the commit history of the project<br>
      <b>git reflog</b>: a <b>local</b> logbook of every time a ref's position (like HEAD) has been changed
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:38%">
        <col style="width:62%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git reflog (show) HEAD</b></td>
          <td>Shows the reflog for HEAD. <b>show</b> is the default subcommand</td>
        </tr>
        <tr>
          <td><b>git reflog &lt;branchname&gt;</b></td>
          <td>e.g. <b>git reflog show myFeatureBranch</b></td>
        </tr>
        <tr>
          <td><b>git reflog &lt;remote&gt;/&lt;branch&gt;</b></td>
          <td>e.g. <b>git reflog show origin/main</b></td>
        </tr>
        <tr>
          <td><b>git reflog stash</b></td>
          <td>Shows the history of your stash</td>
        </tr>
        <tr>
          <td><b>git reflog master@{yesterday}</b></td>
          <td>Shows the reflog position for master yesterday and before that</td>
        </tr>
        <tr>
          <td><b>git diff master@{yesterday} master</b></td>
          <td>Shows the diff between the master branch right now and master yesterday</td>
        </tr>
        <tr>
          <td><b>git log master@{yesterday}..master</b></td>
          <td>Shows which commits have been added since yesterday</td>
        </tr>
        <tr>
          <td><b>git checkout master@{yesterday}</b></td>
          <td>Goes into detached HEAD mode at yesterday's commit on master</td>
        </tr>
      </tbody>
    </table>

    <div class="info">
      Time reference options are e.g.:
      <ul>
        <li>@{yesterday}</li>
        <li>@{1.day.ago}</li>
        <li>@{8.minutes.ago}</li>
        <li>@{2024-01-15T14:30:00}</li>
        <li>@{2024-01-15 14:30:00}</li>
      </ul>
    </div>

    <pre><code class="language-bash">
  # The logbook of HEAD
  git reflog
  git reflog show HEAD

  # The logbook of a specific branch or remote branch
  git reflog show myFeatureBranch
  git reflog show origin/main

  # The logbook of your stashes
  git reflog stash

  # Time based references
  git reflog master@{yesterday}
  git diff master@{yesterday} master
  git log master@{yesterday}..master
  git checkout master@{yesterday}
    </code></pre>
  </div>

  <div data-id="reflog-restoring lost work">
    <h2>Restoring lost work</h2>

    <h3>Restoring a lost commit</h3>

    <div class="info">
      In case you have lost a commit (e.g. due to <b>git reset --hard</b>), you can still get it back
      through the reflog.
      <ol>
        <li>Use <b>git reflog &lt;branchname&gt;</b> (or HEAD)</li>
        <li>Find the commit there and note its reflog number (e.g. master@{1}) or its hash</li>
        <li>Use <b>git reset --hard master@{1}</b> to restore the commit</li>
      </ol>
    </div>

    <pre><code class="language-bash">
  # Oops
  git reset --hard HEAD~3

  # Find where the branch was before the reset
  git reflog show master

  # Put the branch back
  git reset --hard master@{1}
    </code></pre>

    <h3>Restoring after a rebase</h3>

    <div class="info">
      In case you have rebased a bunch of commits, you can retrieve the old ones the same way.
      <ol>
        <li>Use <b>git reflog &lt;branchname&gt;</b> (or HEAD)</li>
        <li>Find the last (most recent) commit that was still valid and note its reflog number or hash</li>
        <li>Use <b>git reset --hard master@{1}</b> to restore that commit</li>
        <li>
          All the <b>parent commits</b> of that commit will automatically appear back as well, since each
          commit file also stores its parent commit. And since the commit is back in the known git log
          history, its references like the parent commit are valid again as well.
        </li>
      </ol>
    </div>

    <pre><code class="language-bash">
  # Undo an unwanted rebase
  git reflog show feature
  git reset --hard feature@{1}
    </code></pre>
  </div>
</div>


<!-- ALIASES -->

<div data-id="git-aliases" class="level1-container">
  <h1 class="header">Aliases</h1>

  <div data-id="aliases-creating aliases">
    <h2>Creating aliases</h2>

    <div class="info">
      Aliases are custom commands. They are stored in the global <b>.gitconfig</b> file in your home directory.
      There are two ways to add one.
    </div>

    <h3>Using the .gitconfig file</h3>

    <div class="info">
      <ol>
        <li>Add an <b>[alias]</b> section to the .gitconfig file</li>
        <li>Under that section, add the name of the alias, followed by <b>=</b>, followed by the command (omit the 'git' keyword)</li>
      </ol>
    </div>

    <pre><code class="language-bash">
  # ~/.gitconfig

  [alias]
      s = status
      l = log
      cm = commit -m
      lg = log --oneline --all --graph --decorate
    </code></pre>

    <h3>Using the command line</h3>

    <div class="info">
      Set the global git config through the command line, the same way as setting your name or email.
    </div>

    <pre><code class="language-bash">
  git config --global alias.&lt;youraliasname&gt; &lt;command&gt;

  # Examples
  git config --global alias.s status
  git config --global alias.l log
  git config --global alias.cm "commit -m"
    </code></pre>

    <h3>Arguments</h3>

    <div class="info">
      Arguments (such as a commit message or filename) are automatically passed to the alias,
      so you can just add them after the alias the same way you would if there were no alias.
    </div>

    <pre><code class="language-bash">
  # Instead of: git commit -m "my commit message"
  git cm "my commit message"

  # Instead of: git status
  git s
    </code></pre>
  </div>
</div>


<!-- BEHIND THE SCENES -->

<div data-id="git-behind the scenes" class="level1-container">
  <h1 class="header">Behind the scenes</h1>

  <div data-id="behind the scenes-the local config file">
    <h2>The local config file</h2>

    <div class="info">
      The local <b>config</b> file can be found in the hidden <b>.git</b> folder.
      It only affects the repo it is in.
    </div>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:40%">
        <col style="width:60%">
      </colgroup>
      <thead>
        <tr>
          <th>Command</th>
          <th>Info</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>git config --local &lt;setting&gt;</b></td>
          <td>Displays the value for the given setting</td>
        </tr>
        <tr>
          <td><b>git config --local &lt;setting&gt; &lt;value&gt;</b></td>
          <td>Sets the value of the setting in the local (repo's) config file</td>
        </tr>
      </tbody>
    </table>

    <pre><code class="language-bash">
  # Read a local setting
  git config --local user.name

  # Write a local setting
  git config --local user.name "Repo Specific Name"
    </code></pre>
  </div>

  <div data-id="behind the scenes-the refs directory">
    <h2>The refs directory</h2>

    <h3>refs/heads/</h3>

    <div class="info">
      - Contains 1 file per branch. The filename is the branch name<br>
      - A branch name file contains just 1 commit hash, which is the <b>tip</b> of that branch
    </div>

    <h3>refs/tags/</h3>

    <div class="info">
      - Contains 1 file per tag. The tag name is the filename<br>
      - A tag name file contains just 1 commit hash, which is the commit the tag is put on
    </div>

    <h3>refs/remotes/&lt;remotename&gt;/</h3>

    <div class="info">
      - Contains 1 file per remote branch. The branch name is the filename<br>
      - A remote branch name file contains just 1 commit hash, which is the commit the remote was on at the
      moment of the last fetch<br>
      - It is possible the actual remote branch is ahead of the commit in the local file.
      You can update it using a <b>fetch</b>
    </div>

    <pre><code class="language-bash">
  .git/
  ├── HEAD
  ├── config
  └── refs/
      ├── heads/
      │   ├── main            # contains one commit hash: the tip of main
      │   └── feature-login   # contains one commit hash: the tip of feature-login
      ├── tags/
      │   └── v1.0.0          # contains one commit hash: the tagged commit
      └── remotes/
          └── origin/
              ├── main        # last known commit of origin/main (updated by fetch)
              └── staging
    </code></pre>
  </div>

  <div data-id="behind the scenes-the head file">
    <h2>The HEAD file</h2>

    <div class="info">
      - When <b>not</b> in detached HEAD, this file contains a reference to
      <b>refs/heads/&lt;branchyouareon&gt;</b>, which in turn holds a commit hash for the tip of that branch<br>
      - When in detached HEAD, this file contains the commit hash to which the detached HEAD is pointing
    </div>

    <pre><code class="language-bash">
  # .git/HEAD while on the main branch (symbolic ref)
  ref: refs/heads/main

  # .git/HEAD while in detached HEAD state (direct ref)
  a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0
    </code></pre>
  </div>

  <div data-id="behind the scenes-blobs trees and commits">
    <h2>Blobs, trees and commits</h2>

    <div class="info">
      - The <b>content of files</b> is stored in <b>blobs</b> (binary large objects)<br>
      - The <b>contents of directories</b> are stored in <b>trees</b>. A tree can hold hashes of other blobs
      and trees. So a tree is basically a directory that holds the references to its contents<br>
      - A <b>commit</b> file contains a tree, a hash link to the parent commit, and metadata such as the
      author and the commit message
    </div>

    <pre><code class="language-none">
    commit
      ├── tree          -> the root directory snapshot
      ├── parent        -> hash of the previous commit
      ├── author        -> name, email, timestamp
      └── message       -> the commit message

    tree (root)
      ├── blob  index.html
      ├── blob  README.md
      └── tree  src/
            ├── blob  app.js
            └── blob  helper.js
    </code></pre>
  </div>
</div>


<!-- WORKFLOWS -->

<div data-id="git-workflows" class="level1-container">
  <h1 class="header">Workflows</h1>

  <div data-id="workflows-a regular feature flow">
    <h2>A regular feature flow</h2>

    <div class="info">
      <b>1.</b> User 1 checks if there is anything new:<br>
      git pull origin main<br><br>

      Then does work and creates a branch:<br>
      git switch -c user1branch<br><br>

      User 1 pushes the branch to show colleague 2:<br>
      git add .<br>
      git commit -m "mymessage"<br>
      git remote -v<br>
      git push origin user1branch
    </div>

    <div class="info">
      <b>2.</b> User 2 works on his own branch:<br>
      git pull origin main<br>
      git switch -c user2branch<br>
      git add .<br>
      git commit -m "user2message"<br>
      git push origin user2branch
    </div>

    <div class="info">
      <b>3.</b> User 2 wants to check user 1's branch to help him out.
      He will fetch first just to check it out:<br>
      git fetch origin<br>
      git branch -r<br>
      git checkout origin/user1branch<br><br>

      This puts him in detached HEAD state, which makes his working directory <b>reflect</b> the state of
      the remote-tracking branch. User 2 can now view his colleague's code but not change it.
      To do so he has to create it locally:<br>
      git switch user1branch<br><br>

      User 2 fixes and pushes back:<br>
      git add .<br>
      git commit -m "fix for user1"<br>
      git push origin user1branch<br><br>

      Then switches back to his own branch to continue his work:<br>
      git switch user2branch
    </div>

    <div class="info">
      <b>4.</b> User 1 is happy and gets the changes from remote:<br>
      git pull origin user1branch
    </div>

    <pre><code class="language-bash">
  # 1. User 1
  git pull origin main
  git switch -c user1branch
  git add .
  git commit -m "mymessage"
  git remote -v
  git push origin user1branch

  # 2. User 2
  git pull origin main
  git switch -c user2branch
  git add .
  git commit -m "user2message"
  git push origin user2branch

  # 3. User 2 helps out on user 1's branch
  git fetch origin
  git branch -r
  git checkout origin/user1branch
  git switch user1branch
  git add .
  git commit -m "fix for user1"
  git push origin user1branch
  git switch user2branch

  # 4. User 1 gets the fix
  git pull origin user1branch
    </code></pre>
  </div>

  <div data-id="workflows-a flow with merge conflicts">
    <h2>A flow with merge conflicts</h2>

    <div class="info">
      1. User1 creates a feature branch called newBranch from master and edits the file index.html<br>
      2. User2 also made changes to index.html, pushes and merges into master<br>
      3. Without pulling latest master, User1 pushes newBranch and creates a PR. There are merge conflicts because master changed<br>
      4. User1 fixes the conflicts with these steps:
      <ul>
        <li><b>git fetch origin</b>: pulls the latest updates from the remote, including the updated master branch, without merging into the local working dir</li>
        <li><b>git switch newBranch</b>: switches to the branch with user1's changes</li>
        <li><b>git merge master</b>: merges master into newBranch and triggers the merge conflicts</li>
        <li>Fix the conflicts in the IDE</li>
        <li><b>git switch master</b>: switches back to master</li>
        <li><b>git merge newBranch</b>: merges the conflict free newBranch into master</li>
        <li><b>git push origin master</b>: pushes the resolved master branch to the remote</li>
      </ul>
    </div>

    <pre><code class="language-bash">
  git fetch origin
  git switch newBranch
  git merge master
  # fix the conflicts in the IDE, then stage and commit

  git switch master
  git merge newBranch
  git push origin master
    </code></pre>
  </div>

  <div data-id="workflows-good to know">
    <h2>Good to know</h2>

    <div class="info">
      - Switching branches when having tracked uncommitted files will produce an error (the changes would be lost since the file will revert to the other branch without having the changes for the current branch tracked)<br>
      - Switching branches when having an untracked file will take the untracked file to the switched branch<br>
      - Branches are merged, not commits<br>
      - Creating a new branch is always done from the current HEAD<br>
      - Merges always happen into the current HEAD (HEAD must be on the receiving branch; to merge into master, master must be HEAD)<br>
      - After resolving merge conflicts, you still have to stage and commit the merged changes<br>
      - Git shorthand commands can be concatenated (git commit -a -m and git commit -am do the same)<br>
      - When VS Code (or another IDE) has been setup as the difftool, you can run every diff command using <b>git difftool</b> instead of <b>git diff</b> to open VS Code and view differences (e.g. git difftool HEAD)<br>
      - It is possible to stash multiple things; view them with git stash list<br>
      - Applying stashed changes can result in merge conflicts (handled like normal merge conflicts)<br>
      - Commits don't get pushed; an entire branch gets pushed<br>
      - Tags are not pushed to the remote by default
    </div>

    <div class="info">
      Switching branches with uncommitted changes can cause 2 things:
      <ul>
        <li>No conflicts: the changes come with you to the other branch (and switching back takes them back)</li>
        <li>Conflicts: Git won't let you switch because there are uncommitted changes</li>
      </ul>
      If you don't want to commit yet, you can stash the changes and then switch without losing them.
    </div>
  </div>
</div>


<!-- TERMINOLOGY -->

<div data-id="git-terminology" class="level1-container">
  <h1 class="header">Terminology</h1>

  <div data-id="terminology-glossary">
    <h2>Glossary</h2>

    <div class="info">
      <b>Working directory</b>: the folder you are working in<br>
      <b>Staging area</b>: an area where changes can be added so they can be bundled into commits<br>
      <b>index</b>: synonym for staging<br>
      <b>Repository</b>: the directory that contains the .git folder and everything in it<br>
      <b>tracked file</b>: a file Git monitors because it was added with git add, or it was committed previously
    </div>

    <div class="info">
      <b>HEAD</b>: a pointer to the commit that will serve as the <b>parent for your next commit</b>, with two possible forms:
      <ul>
        <li><b>Symbolic ref</b> (normal state): HEAD points to the name of your current branch (e.g. main), and that branch in turn points to the most recent commit. This is the state during normal work.</li>
        <li><b>Direct ref</b> (detached HEAD): HEAD points directly to a commit hash, without a branch in between. This occurs e.g. after git checkout &lt;commit-hash&gt;.</li>
      </ul>
      <b>HEAD -&gt; master</b>: the current HEAD is the master branch<br>
      <b>HEAD^</b>: HEAD's parent commit (commit before the last commit)<br>
      <b>HEAD~&lt;n&gt;</b>: nth previous commit before the last (e.g. HEAD~1 is same as HEAD^)<br>
      <b>detached HEAD</b>: not on a branch, looking at a specific commit. You can commit, but those commits may be lost when switching unless you create a branch (git switch -c my-experiment)
    </div>

    <div class="info">
      <b>Fast forward merge</b>: happens when there have been no new commits on the receiving branch since the feature branch was created (or last diverged). Merging just moves the pointer forward<br>
      <b>Non-fast forward merge without conflicts</b>: both branches have changes, but they do not conflict (e.g. different files). Creates a merge commit with two parents<br>
      <b>Non-fast forward merge with conflicts</b>: both branches have changes and they conflict (e.g. same line). Requires manual resolution and then commit
    </div>

    <div class="info">
      <b>Remote</b>: a URL where the repo lives in the cloud (e.g. https://github.com/username/cool-project.git)<br>
      <b>origin</b>: an alias for the URL of the remote repo that your local git repo is connected to<br>
      <b>remote tracking branch</b>: a <b>local reference</b> to the state of a branch on a remote repository. It functions as a bookmark pointing to the last known commit of the branch on origin<br>
      <b>upstream branch</b>: a branch on the remote that is linked to the local one. Linked branches let you shorten commands (git push instead of git push origin mybranch)<br>
      <b>fetching</b>: getting the changes from a remote repo, but without integrating them automatically into your working files (puts them in the local repo, not the working dir). This way you can view what others have been working on without merging those changes into your local repo<br>
      <b>Pull request (PR)</b>: a feature of products like GitHub and Bitbucket. It is not native to git itself. It allows team members to review and approve or reject work on a given branch that is ready to be merged into main<br>
      <b>Fork</b>: a personal copy of an external repo that is put under your own GitHub account. You can edit it freely without influencing the original repo. It is often used to contribute to external repos that you do not have push rights to
    </div>

    <div class="info">
      <b>Rebase</b>: a command that can be used as an alternative to merge. Merging into a branch usually creates a merge commit, which can pollute the commit history. Rebasing re-applies your branch's commits on top of the target branch. That way the history just looks like the target branch commits followed by the current branch's commits<br>
      <b>Base commit</b>: the commit from which your current branch started (was split off from another branch). Every branch (except main) has a base commit, which is a commit on the branch the current branch originated from<br>
      <b>Tag</b>: a pointer to a specific commit, normally used for versioning<br>
      <b>Reflog</b>: a local logbook of every time a ref's position (like HEAD) has been changed
    </div>
  </div>
</div>


<!-- BEST PRACTICES -->

<div data-id="git-best practices" class="level1-container">
  <h1 class="header">Best practices</h1>

  <div data-id="best practices-commits and branches">
    <h2>Commits and branches</h2>

    <div class="info">
      <b>A commit should hold a single feature, change or fix</b> (atomic commits).
    </div>

    <div class="info">
      Working only on a main branch while collaborating is not ideal:
      <ul>
        <li>To push you need to be on the latest version of main, which means pulling before pushing. This leads to time spent resolving conflicts.</li>
        <li>Every push disturbs the main codebase, making it hard to experiment, add large changes, or push incomplete work. If your push breaks the program, it breaks it for everyone.</li>
      </ul>
    </div>

    <div class="info">
      Work should be done on feature branches. The main branch should be treated as the official project history that does not contain broken or experimental code.
    </div>

    <div class="info">
      Never rebase commits that have been pushed to a shared branch. Rebase freely on local or personal
      feature branches; use merge (or a squash merge) when integrating into shared branches like main.
    </div>
  </div>

  <div data-id="best practices-analogy">
    <h2>Analogy</h2>

    <table class="FormatTableSmall">
      <colgroup>
        <col style="width:34%">
        <col style="width:66%">
      </colgroup>
      <thead>
        <tr>
          <th>Git</th>
          <th>Analogy</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><b>Working directory</b></td>
          <td>The letter you are writing</td>
        </tr>
        <tr>
          <td><b>Staging area</b></td>
          <td>The letter(s) you put into an envelope</td>
        </tr>
        <tr>
          <td><b>Local repository</b></td>
          <td>The sealed envelope</td>
        </tr>
        <tr>
          <td><b>Remote</b></td>
          <td>The envelope has been dropped in the mailbox</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

`;
